import type { ReactNode } from "react";
import { addHours, format } from "date-fns";
import { Pencil, Trash2, Truck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DataCard } from "@/components/ui/data-card";
import type { Location, Tractor, WorkOrder } from "@/types/tms";

export type WorkOrderCardWorkOrder = WorkOrder & {
  tractor: (Tractor & { home_base: Location | null }) | null;
};

interface WorkOrderCardProps {
  workOrder: WorkOrderCardWorkOrder;
  statusBadge: ReactNode;
  onEdit: (workOrder: WorkOrderCardWorkOrder) => void;
  onDelete: (id: string) => void;
}

/** Mobile/tablet representation of a work order row on the Maintenance page. */
export function WorkOrderCard({ workOrder, statusBadge, onEdit, onDelete }: WorkOrderCardProps) {
  const startTime = new Date(workOrder.start_time);
  const endTime = addHours(startTime, workOrder.duration_hours);

  return (
    <DataCard>
      <DataCard.Header>
        <span className="font-mono text-sm">{workOrder.unique_id}</span>
        {statusBadge}
        <span className="ml-auto flex items-center gap-1">
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onEdit(workOrder)}>
            <Pencil className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() => {
              if (confirm("Delete this work order?")) onDelete(workOrder.id);
            }}
          >
            <Trash2 className="h-4 w-4 text-destructive" />
          </Button>
        </span>
      </DataCard.Header>

      <DataCard.Body>
        <DataCard.Field icon={Truck} label="Tractor">
          {workOrder.tractor?.name || "Unknown"}
        </DataCard.Field>
        <DataCard.Field label="Duration">{workOrder.duration_hours}h</DataCard.Field>
        <DataCard.Field
          label="Start time"
          secondary={format(startTime, "h:mm a")}
        >
          {format(startTime, "MMM d, yyyy")}
        </DataCard.Field>
        <DataCard.Field
          label="Expected back"
          secondary={format(endTime, "h:mm a")}
        >
          {format(endTime, "MMM d, yyyy")}
        </DataCard.Field>
      </DataCard.Body>

      <DataCard.Body columns={1}>
        <DataCard.Field label="Description">{workOrder.description || "—"}</DataCard.Field>
      </DataCard.Body>
    </DataCard>
  );
}